import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Grid2 as Grid, TextField } from "@mui/material";
import { UseFormReturn } from "react-hook-form";
import OnboardingFormSection from "@components/Onboarding/OnboardingForm/OnboardingFormSection";
import {
  FamilyMemberFormType,
  familyMemberToFormType,
} from "@utils/patientFormHelper";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import ValidationError from "@components/ValidationError/ValidationError";
import ReactHookFormDatePicker from "@components/ReactHookFormDatePicker/ReactHookFormDatePicker";
import GenderSelect from "@components/GenderSelect/GenderSelect";
import CustomLabelInput from "@components/CustomLabelInput";
import ResidenciesForm from "@components/ResidenciesForm";
import OccupationsForm from "@components/OccupationsForm/OccupationsForm";
import { useGetPatientQuery } from "@store/patientApi";

const FamilyMemberForm: FC<{ form: UseFormReturn<FamilyMemberFormType> }> = ({
  form,
}) => {
  const { t } = useTranslation();
  const { familyMemberId } = useParams();
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: patient } = useGetPatientQuery(patientId!, {
    skip: !patientId,
  });
  const familyMember = useMemo(
    () => patient?.familyMembers.find(({ id }) => id === familyMemberId),
    [patient, familyMemberId],
  );

  const {
    control,
    reset,
    register,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (familyMember) {
      reset(familyMemberToFormType(familyMember));
    }
  }, [familyMember, reset]);

  return (
    <>
      <OnboardingFormSection title={t("personForm.identity")} marginTop={false}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <CustomLabelInput
              label={t("personForm.nicknameInput.label")}
              id="familyMemberNickname"
            >
              <TextField
                {...register(`nickname`)}
                error={!!errors?.nickname}
                id="familyMemberNickname"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.nicknameInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>
          <Grid size={6}>
            <GenderSelect
              label={t("personForm.genderInput.label")}
              control={control}
              name={"gender"}
              required={true}
              error={!!errors?.gender}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 4 }}>
            <CustomLabelInput
              label={t("personForm.firstNameInput.label")}
              id="familyMemberFirstName"
              required={true}
            >
              <TextField
                {...register(`firstName`, {
                  required: true,
                })}
                error={!!errors?.firstName}
                id="familyMemberFirstName"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.firstNameInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <CustomLabelInput
              label={t("personForm.middleNameInput.label")}
              id="familyMemberMiddleName"
            >
              <TextField
                {...register(`middleName`)}
                error={!!errors?.middleName}
                id="familyMemberMiddleName"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.middleNameInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <CustomLabelInput
              label={t("personForm.lastNameInput.label")}
              id="familyMemberLastName"
            >
              <TextField
                {...register(`lastName`)}
                error={!!errors?.lastName}
                id="familyMemberLastName"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.lastNameInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>
        </Grid>
      </OnboardingFormSection>

      <OnboardingFormSection title={t("personForm.birth")}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <CustomLabelInput
              label={t("personForm.birthDateInput.label")}
              id="familyMemberBirthDate"
            >
              <ReactHookFormDatePicker
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!errors?.birthDate,
                  },
                }}
                id="familyMemberBirthDate"
                controller={{
                  name: "birthDate",
                  control,
                  defaultValue: null,
                }}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={6}>
            <CustomLabelInput
              label={t("personForm.birthLocationInput.label")}
              id="birthLocation"
            >
              <TextField
                {...register("birthLocation")}
                error={!!errors?.birthLocation}
                id="birthLocation"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.birthLocationInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>
        </Grid>
      </OnboardingFormSection>

      <OnboardingFormSection title={t("personForm.about")}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <CustomLabelInput
              label={t("personForm.bioInput.label")}
              id="familyMemberBio"
            >
              <TextField
                {...register("bio")}
                error={!!errors?.bio}
                id="familyMemberBio"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                multiline
                minRows={3}
                placeholder={t("personForm.bioInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={12}>
            <CustomLabelInput
              label={t("personForm.hobbiesInput.label")}
              id="familyMemberHobbies"
            >
              <TextField
                {...register("hobbies")}
                error={!!errors?.hobbies}
                id="familyMemberHobbies"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.hobbiesInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>
        </Grid>
      </OnboardingFormSection>

      <OnboardingFormSection title={t("personForm.relation")}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <CustomLabelInput
              label={t("personForm.relationInput.label")}
              id="familyMemberRelation"
              required={true}
            >
              <TextField
                {...register(`relation`, { required: true })}
                error={!!errors?.relation}
                id="familyMemberRelation"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.relationInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <CustomLabelInput
              label={t("personForm.relationToInput.label")}
              id="familyMemberRelationTo"
              required={true}
            >
              <TextField
                {...register(`relationTo`, { required: true })}
                error={!!errors?.relationTo}
                id="familyMemberRelationTo"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("personForm.relationToInput.placeholder")}
              />
            </CustomLabelInput>
          </Grid>
        </Grid>
      </OnboardingFormSection>

      <OnboardingFormSection title={t("personForm.residencies")}>
        <ResidenciesForm form={form} />
      </OnboardingFormSection>

      <OnboardingFormSection title={t("personForm.occupations")}>
        <OccupationsForm form={form} />
      </OnboardingFormSection>

      {errors?.root && (
        <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
      )}
    </>
  );
};

export default FamilyMemberForm;
